import { buildProductShippingFaqAnswer } from "@/features/shipping/server/buildShippingMessaging";
import { DEFAULT_SHIPPING_SETTINGS } from "@/features/shipping/shared/defaults";
import type { ShippingSettings } from "@/features/shipping/shared/types";
import type { BlogForClient, ProductForClient } from "@/lib/shared/types/interfaces";
import { truncateMetaDescription } from "./seo";
import { getTextFromObject } from "./utils/richText";

const materialPatterns = [
  { pattern: /\b925\b|\bsterling silver\b/i, value: "925 sterling silver" },
  { pattern: /\bmother[- ]of[- ]pearl\b/i, value: "mother-of-pearl" },
  { pattern: /\bbaroque pearl\b/i, value: "baroque pearl" },
  { pattern: /\bfreshwater pearl\b/i, value: "freshwater pearl" },
  { pattern: /\bpearl\b/i, value: "pearl" },
  { pattern: /\blapis lazuli\b/i, value: "lapis lazuli" },
  { pattern: /\btiger'?s eye\b/i, value: "tiger's eye" },
  { pattern: /\bblack agate\b/i, value: "black agate" },
  { pattern: /\bred agate\b/i, value: "red agate" },
  { pattern: /\bagate\b/i, value: "agate" },
  { pattern: /\bopal\b/i, value: "opal" },
  { pattern: /\bcrystal\b/i, value: "crystal" },
] as const;

const occasionPatterns = [
  { pattern: /\bgift|gift-ready|present\b/i, value: "gift" },
  { pattern: /\banniversary\b/i, value: "anniversary" },
  { pattern: /\bbirthday\b/i, value: "birthday" },
  { pattern: /\bwedding\b/i, value: "wedding" },
  { pattern: /\bvalentine\b/i, value: "Valentine's Day" },
  { pattern: /\bchristmas\b/i, value: "Christmas" },
] as const;

const audiencePatterns = [
  { pattern: /\bwomen|woman|her\b/i, value: "women" },
  { pattern: /\bbride|bridal\b/i, value: "bride" },
  { pattern: /\bminimalist\b/i, value: "minimalist jewellery lovers" },
  { pattern: /\bvintage\b/i, value: "vintage jewellery lovers" },
] as const;

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function uniqueValues(values: string[]): string[] | undefined {
  const seen = new Set<string>();
  const unique = values.filter((value) => {
    const key = value.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return unique.length ? unique : undefined;
}

function inferValues(text: string, patterns: readonly { pattern: RegExp; value: string }[]) {
  return uniqueValues(
    patterns.filter(({ pattern }) => pattern.test(text)).map(({ value }) => value),
  );
}

function getProductSourceText(product: ProductForClient): string {
  return normalizeWhitespace(
    [
      product.name,
      product.sku,
      product.labels?.join(" "),
      product.searchableText,
      product.description ? getTextFromObject(product.description) : "",
    ]
      .filter(Boolean)
      .join(" "),
  );
}

export function buildProductSeoTitle(product: ProductForClient): string {
  return `${product.name} (${product.sku})`;
}

export function buildProductSeoDescription(
  product: ProductForClient,
  fallbackDescription?: string,
): string | undefined {
  const description =
    (product.description ? getTextFromObject(product.description) : undefined) ??
    product.searchableText ??
    fallbackDescription;

  if (!description) return fallbackDescription;
  return truncateMetaDescription(`${product.name} (${product.sku}). ${description}`);
}

export function buildProductAiSummary(product: ProductForClient): string | undefined {
  const source = buildProductSeoDescription(product);
  if (!source) return undefined;

  const labels = product.labels?.length ? ` Categories: ${product.labels.join(", ")}.` : "";
  return truncateMetaDescription(`${source}${labels}`, 220);
}

export function inferProductMaterials(product: ProductForClient): string[] | undefined {
  return inferValues(getProductSourceText(product), materialPatterns);
}

export function inferProductGiftOccasions(product: ProductForClient): string[] | undefined {
  const inferred = inferValues(getProductSourceText(product), occasionPatterns);
  return inferred ?? ["gift"];
}

export function inferProductAudience(product: ProductForClient): string[] | undefined {
  return inferValues(getProductSourceText(product), audiencePatterns);
}

export function buildProductCareInstructions(product: ProductForClient): string | undefined {
  const materials = inferProductMaterials(product);
  if (!materials?.length) return undefined;

  if (materials.some((material) => /pearl|silver|opal|agate|crystal|lazuli|eye/i.test(material))) {
    return "Keep dry, avoid perfume and harsh chemicals, and store separately in a soft pouch.";
  }

  return undefined;
}

export function buildBlogSeoTitle(blog: BlogForClient): string {
  return blog.title;
}

export function buildBlogSeoDescription(blog: BlogForClient): string | undefined {
  return truncateMetaDescription(getTextFromObject(blog.introduction));
}

export function buildProductSeoHighlights(product: ProductForClient): string[] {
  const highlights: string[] = [];
  const materials = inferProductMaterials(product);
  const occasions = inferProductGiftOccasions(product);
  const audience = inferProductAudience(product);
  const care = buildProductCareInstructions(product);

  if (materials?.length) {
    highlights.push(`Materials: ${materials.join(", ")}.`);
  }

  if (occasions?.length) {
    highlights.push(`Best for: ${occasions.join(", ")}.`);
  }

  if (audience?.length) {
    highlights.push(`Style fit: ${audience.join(", ")}.`);
  }

  if (care) {
    highlights.push(`Care: ${care}`);
  }

  return highlights;
}

export async function buildProductFaqItems(
  product: ProductForClient,
  _shippingSettings: ShippingSettings = DEFAULT_SHIPPING_SETTINGS,
  locale = "en",
) {
  const shippingAnswer = await buildProductShippingFaqAnswer(locale);
  const materials = inferProductMaterials(product)?.join(", ");
  const occasions = inferProductGiftOccasions(product)?.join(", ");
  const care =
    buildProductCareInstructions(product) ??
    "Keep it dry, avoid perfume and harsh chemicals, and store it separately in a soft pouch.";

  return [
    {
      question: `Is ${product.name} gift-ready?`,
      answer: occasions
        ? `${product.name} works well for ${occasions} and arrives as gift-ready jewellery for easy gifting.`
        : `${product.name} is gift-ready jewellery for birthdays, anniversaries, and special occasions.`,
    },
    {
      question: `What materials are used in ${product.name}?`,
      answer: materials
        ? `${product.name} includes ${materials}. Check product variants for the exact style and finish.`
        : `Check product variants for the exact material and finish used in ${product.name}.`,
    },
    {
      question: `How do I care for ${product.name}?`,
      answer: care,
    },
    {
      question: `Does ${product.name} ship worldwide?`,
      answer: shippingAnswer,
    },
  ];
}
